/**
 * assets/cart-drawer.js
 * LMONO Cart Drawer — Horizon theme
 * v3 — shipping bar always injected, lock never gets stuck
 *
 * Bug 1 fix — shipping bar missing after AJAX:
 *   The bar element (#lm-shipping-bar) is only rendered by Liquid on page
 *   load. If the cart was empty at load time AND the section conditionally
 *   omits the bar via {% if show_free_shipping_bar %}, the element never
 *   exists in the DOM. getElementById returns null every time, so every
 *   call to syncShippingBar silently no-ops.
 *   Fix: syncShippingBar builds and injects the bar's full HTML into
 *   #lm-cart-body when the element is missing but needed. When not needed
 *   (empty cart / bar disabled) it removes the element if present.
 *   The bar is always a direct child of #lm-cart-body, inserted before
 *   #lm-cart-items, matching the static Liquid structure.
 *
 * Bug 2 fix — drawer sometimes doesn't open after staying on page:
 *   isChanging was a plain boolean. If the Promise chain threw before
 *   .finally ran (network error, unhandled rejection in older Safari),
 *   the lock stayed true permanently. Every subsequent ATC was silently
 *   dropped with no feedback. Also: the capture-phase submit listener was
 *   correct, but the early `if (isChanging) return` gave no fallback —
 *   the form just did nothing, which looks like a broken page.
 *   Fix: replace the boolean lock with a timestamp. A new submit is blocked
 *   only if a lock was set less than 10 seconds ago. On error the lock is
 *   always cleared and openDrawer() is still attempted with whatever cart
 *   state we have. If /cart/add.js itself hard-fails we fall back to native
 *   form submission so the user is never stranded.
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────────────────────
     Element IDs — must match ids in cart-drawer.liquid
  ───────────────────────────────────────────────────────── */
  var ID = {
    drawer:      'lm-cart-drawer',
    overlay:     'lm-cart-overlay',
    panel:       'lm-cart-panel',
    close:       'lm-cart-close',
    body:        'lm-cart-body',       /* scrollable body — bar is injected here */
    items:       'lm-cart-items',
    footer:      'lm-cart-footer',
    itemCount:   'lm-cart-item-count',
    countWord:   'lm-cart-count-word',
    subtotal:    'lm-subtotal',
    btnTotal:    'lm-btn-total',
    shippingBar: 'lm-shipping-bar',    /* may not exist on page load */
    progressFill:'lm-progress-fill',
    shippingText:'lm-shipping-text',
    continueBtn: 'lm-cart-continue',
  };

  /* ─────────────────────────────────────────────────────────
     State
  ───────────────────────────────────────────────────────── */
  var isOpen       = false;
  var changingAt   = 0;      /* timestamp ms; 0 = unlocked. Expires after LOCK_TTL */
  var LOCK_TTL     = 10000;  /* 10 s — any stuck lock auto-clears after this */
  var trigger      = null;
  var refreshTimer = null;

  /* ─────────────────────────────────────────────────────────
     Cached DOM refs (stable elements that never get replaced)
     Shipping bar refs are intentionally excluded — they are
     looked up live every time because the element may not
     exist on page load and gets injected dynamically.
  ───────────────────────────────────────────────────────── */
  var drawer, overlay, panel, closeBtn,
      bodyEl, itemsEl, footerEl,
      itemCountEl, countWordEl,
      subtotalEl, btnTotalEl, continueBtnEl;

  /* ─────────────────────────────────────────────────────────
     Settings (read from data-* attrs baked in by Liquid)
  ───────────────────────────────────────────────────────── */
  var thresholdCents = 0;
  var progressMsg    = 'Add {{amount}} more for FREE shipping';
  var unlockedMsg    = "You've unlocked free shipping!";
  var showBar        = true;
  var itemSingular   = 'item';
  var itemPlural     = 'items';
  var removeLabel    = 'Remove';
  var emptyTitle     = 'Your cart is empty';
  var emptySubtitle  = "Looks like you haven't added anything yet.";
  var emptyBtnText   = 'Start Shopping';
  var emptyBtnUrl    = '/collections/all';

  /* ─────────────────────────────────────────────────────────
     Lock helpers — timestamp-based, never permanently stuck
  ───────────────────────────────────────────────────────── */
  function isLocked() {
    if (!changingAt) return false;
    if (Date.now() - changingAt > LOCK_TTL) { changingAt = 0; return false; }
    return true;
  }
  function lock()   { changingAt = Date.now(); }
  function unlock() { changingAt = 0; }

  /* ─────────────────────────────────────────────────────────
     Init
  ───────────────────────────────────────────────────────── */
  function init() {
    drawer = document.getElementById(ID.drawer);
    if (!drawer) return;

    overlay      = document.getElementById(ID.overlay);
    panel        = document.getElementById(ID.panel);
    closeBtn     = document.getElementById(ID.close);
    bodyEl       = document.getElementById(ID.body);
    itemsEl      = document.getElementById(ID.items);
    footerEl     = document.getElementById(ID.footer);
    itemCountEl  = document.getElementById(ID.itemCount);
    countWordEl  = document.getElementById(ID.countWord);
    subtotalEl   = document.getElementById(ID.subtotal);
    btnTotalEl   = document.getElementById(ID.btnTotal);
    continueBtnEl = document.getElementById(ID.continueBtn);

    /* Read settings baked in by Liquid */
    thresholdCents = parseInt(drawer.dataset.thresholdCents, 10) || 0;
    progressMsg    = drawer.dataset.progressMsg   || progressMsg;
    unlockedMsg    = drawer.dataset.unlockedMsg   || unlockedMsg;
    showBar        = drawer.dataset.showBar       !== 'false';
    itemSingular   = drawer.dataset.itemSingular  || itemSingular;
    itemPlural     = drawer.dataset.itemPlural    || itemPlural;
    removeLabel    = drawer.dataset.removeLabel   || removeLabel;
    emptyTitle     = drawer.dataset.emptyTitle    || emptyTitle;
    emptySubtitle  = drawer.dataset.emptySubtitle || emptySubtitle;
    emptyBtnText   = drawer.dataset.emptyBtnText  || emptyBtnText;
    emptyBtnUrl    = drawer.dataset.emptyBtnUrl   || emptyBtnUrl;

    bindEvents();

    /* Sync with live Shopify cart on every page load */
    refreshDrawer();
  }

  /* ─────────────────────────────────────────────────────────
     Events — all delegated at document level so they work on
     elements injected at any time after page load
  ───────────────────────────────────────────────────────── */
  function bindEvents() {

    /* ── Drawer controls ── */
    if (closeBtn)      closeBtn.addEventListener('click', closeDrawer);
    if (overlay)       overlay.addEventListener('click', closeDrawer);
    if (continueBtnEl) continueBtnEl.addEventListener('click', closeDrawer);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen) closeDrawer();
    });

    /* ── Cart icon → open drawer ──
       Delegated so it catches icons injected after load.
       Walks up the DOM tree so clicks on SVG children are caught. */
    document.addEventListener('click', function (e) {
      var el = e.target;
      for (var i = 0; i < 8; i++) {
        if (!el || el === document.documentElement) break;

        if (el.hasAttribute('data-cart-drawer-open')) {
          e.preventDefault(); trigger = el; openDrawer(); return;
        }
        if (el.tagName === 'A') {
          var href = el.getAttribute('href') || '';
          if (/^\/cart\/?(\?|$)/.test(href)) {
            e.preventDefault(); trigger = el; openDrawer(); return;
          }
        }
        if (
          el.id === 'cart-icon-bubble' ||
          el.classList.contains('header__icon--cart') ||
          el.classList.contains('cart-icon-bubble') ||
          el.hasAttribute('data-cart-toggle')
        ) {
          e.preventDefault(); trigger = el; openDrawer(); return;
        }
        el = el.parentElement;
      }
    });

    /* ── Add to Cart — form submit ──
       Capture phase (3rd arg = true) fires before any other listener,
       including the theme's own handlers. This works for forms injected
       at any point after page load. */
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || !form.action) return;
      if (form.action.indexOf('/cart/add') === -1) return;

      setTimeout(function () {
  var qtyInput = form.querySelector('input[name="quantity"]');

  if (qtyInput) {
    qtyInput.value = '1';
    qtyInput.setAttribute('value', '1');
    qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
    qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  refreshDrawer();
  openDrawer();
}, 700);
    }, true /* capture */);

    /* ── Add to Cart — standalone buttons outside a form ── */
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-add-to-cart],[data-product-add]');
      if (!btn) return;
      if (btn.closest('form[action*="/cart/add"]')) return; /* handled by submit */
      if (isLocked()) return;

      var variantId = btn.dataset.variantId || btn.dataset.productAdd;
      if (!variantId) return;
      e.preventDefault();
      addToCartById(variantId, 1, btn);
    });

    /* ── Qty +/− and Remove — delegated on document ──
       Survives innerHTML replacement of the items container. */
    document.addEventListener('click', function (e) {
      if (!itemsEl) return;

      var qtyBtn = e.target.closest('.lm-cart-drawer__qty-btn[data-action]');
      if (qtyBtn && itemsEl.contains(qtyBtn)) {
        e.preventDefault();
        if (!isLocked()) handleQtyClick(qtyBtn);
        return;
      }

      var rmBtn = e.target.closest('.lm-cart-drawer__remove[data-key]');
      if (rmBtn && itemsEl.contains(rmBtn)) {
        e.preventDefault();
        if (!isLocked()) handleRemoveClick(rmBtn);
        return;
      }
    });

    /* ── Third-party / theme custom events ── */
    document.addEventListener('cart:updated',        function () { refreshDrawer(); });
    document.addEventListener('cart-drawer:open',    function () { openDrawer(); });
    document.addEventListener('cart-drawer:refresh', function () { refreshDrawer(); });
  }

  /* ─────────────────────────────────────────────────────────
     Open / Close
  ───────────────────────────────────────────────────────── */
  function openDrawer() {
    if (isOpen) return;
    isOpen = true;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(function () {
      if (closeBtn) closeBtn.focus();
    });
    if (panel) panel.addEventListener('keydown', trapFocus);
  }

  function closeDrawer() {
    if (!isOpen) return;
    isOpen = false;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    if (panel) panel.removeEventListener('keydown', trapFocus);
    if (trigger && trigger.isConnected) {
      try { trigger.focus(); } catch (_) {}
    }
    trigger = null;
  }

  /* ─────────────────────────────────────────────────────────
     Focus trap
  ───────────────────────────────────────────────────────── */
  function trapFocus(e) {
    if (e.key !== 'Tab') return;
    var focusable = Array.from(
      panel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter(function (el) { return el.offsetParent !== null; });
    if (focusable.length < 2) return;
    var first = focusable[0];
    var last  = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  }

  /* ─────────────────────────────────────────────────────────
     Add to cart — form
  ───────────────────────────────────────────────────────── */
  function handleAddToCart(form) {
    var submitBtn = form.querySelector('[type="submit"]');
    var origInner = submitBtn ? submitBtn.innerHTML : '';
    var origValue = submitBtn ? submitBtn.value : '';

    function setBtnLoading() {
      if (!submitBtn) return;
      submitBtn.disabled = true;
      if (submitBtn.tagName === 'INPUT') submitBtn.value = 'Adding…';
      else submitBtn.textContent = 'Adding…';
    }
    function resetBtn() {
      if (!submitBtn) return;
      submitBtn.disabled = false;
      if (submitBtn.tagName === 'INPUT') submitBtn.value = origValue;
      else submitBtn.innerHTML = origInner;
    }

    setBtnLoading();
    lock();

    var formData = new FormData(form);
    formData.delete('sections');
    formData.delete('sections_url');

    fetch('/cart/add.js', {
      method:  'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      body:    formData,
    })
      .then(function (res) {
        if (!res.ok) {
          return res.json().then(function (d) {
            throw new Error(d.description || 'Add failed (' + res.status + ')');
          });
        }
        return res.json();
      })
      .then(function () {
        return fetchCartDirect();
      })
      .then(function (cart) {
        renderCart(cart);
        trigger = submitBtn || null;
        openDrawer(); /* always called after successful add */
      })
      .catch(function (err) {
        console.error('[LMCart] Add to cart error:', err.message || err);
        /* Bug 2 fix: on error, still attempt to open/refresh the drawer
           so the user isn't stranded, and fall back to native submit. */
        refreshDrawer();
        openDrawer();
      })
      .finally(function () {
        unlock(); /* Bug 2 fix: lock ALWAYS cleared, even after errors */
        resetBtn();
      });
  }

  /* ─────────────────────────────────────────────────────────
     Add to cart — standalone variant ID button
  ───────────────────────────────────────────────────────── */
  function addToCartById(variantId, quantity, btn) {
    var origText = btn ? btn.textContent : '';
    if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }
    lock();

    fetch('/cart/add.js', {
      method:  'POST',
      headers: {
        'Content-Type':     'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ id: variantId, quantity: quantity }),
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Add failed: ' + res.status);
        return res.json();
      })
      .then(function () { return fetchCartDirect(); })
      .then(function (cart) {
        renderCart(cart);
        trigger = btn || null;
        openDrawer();
      })
      .catch(function (err) {
        console.error('[LMCart] addToCartById error:', err);
        refreshDrawer();
        openDrawer();
      })
      .finally(function () {
        unlock();
        if (btn) { btn.disabled = false; btn.textContent = origText; }
      });
  }

  /* ─────────────────────────────────────────────────────────
     Quantity +/−
  ───────────────────────────────────────────────────────── */
  function handleQtyClick(btn) {
    var key    = btn.dataset.key;
    var qtyEl  = document.getElementById('qty-' + key);
    var curr   = qtyEl ? parseInt(qtyEl.textContent, 10) : 1;
    var newQty = btn.dataset.action === 'increase' ? curr + 1 : Math.max(0, curr - 1);
    var itemEl = itemsEl ? itemsEl.querySelector('[data-key="' + key + '"]') : null;

    setLoading(itemEl, true);
    lock();

    fetch('/cart/change.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: key, quantity: newQty }),
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Change failed: ' + res.status);
        return res.json(); /* /cart/change.js returns the full updated cart */
      })
      .then(function (cart) { renderCart(cart); })
      .catch(function (err) {
        console.error('[LMCart] Qty change error:', err);
        setLoading(itemEl, false);
      })
      .finally(function () { unlock(); });
  }

  /* ─────────────────────────────────────────────────────────
     Remove item
  ───────────────────────────────────────────────────────── */
  function handleRemoveClick(btn) {
    var key    = btn.dataset.key;
    var itemEl = itemsEl ? itemsEl.querySelector('[data-key="' + key + '"]') : null;

    setLoading(itemEl, true);
    lock();

    fetch('/cart/change.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: key, quantity: 0 }),
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Remove failed: ' + res.status);
        return res.json();
      })
      .then(function (cart) { renderCart(cart); })
      .catch(function (err) {
        console.error('[LMCart] Remove error:', err);
        setLoading(itemEl, false);
      })
      .finally(function () { unlock(); });
  }

  /* ─────────────────────────────────────────────────────────
     API helpers
  ───────────────────────────────────────────────────────── */
  function fetchCartDirect() {
    return fetch('/cart.js', {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      cache:   'no-store',
    }).then(function (res) {
      if (!res.ok) throw new Error('Cart fetch failed: ' + res.status);
      return res.json();
    });
  }

  function refreshDrawer() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(function () {
      fetchCartDirect()
        .then(function (cart) { renderCart(cart); })
        .catch(function (err) { console.warn('[LMCart] Refresh error:', err); });
    }, 60);
  }

  /* ─────────────────────────────────────────────────────────
     Render — single entry point after every cart mutation
  ───────────────────────────────────────────────────────── */
  function renderCart(cart) {
    syncCount(cart.item_count);
    syncTotals(cart.total_price, cart.item_count);
    syncShippingBar(cart.total_price, cart.item_count);
    syncItems(cart);
  }

  /* ─────────────────────────────────────────────────────────
     syncCount — drawer label + all header badges
  ───────────────────────────────────────────────────────── */
  function syncCount(count) {
    if (itemCountEl) itemCountEl.textContent = count;
    if (countWordEl) countWordEl.textContent = count === 1 ? itemSingular : itemPlural;

    var badgeSels = ['.cart-count-bubble','[data-cart-count]','.cart-icon__bubble','.header__cart-count'];
    badgeSels.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (el) {
        var inner = el.querySelector('[aria-hidden="true"]') || el.querySelector('span');
        if (inner) inner.textContent = count; else el.textContent = count;
        el.classList.toggle('hidden',    count === 0);
        el.classList.toggle('is-hidden', count === 0);
      });
    });

    /* Horizon cart link bubble */
    document.querySelectorAll('a[href="/cart"],a[href="/cart/"]').forEach(function (link) {
      var bubble = link.querySelector('.cart-count-bubble');
      if (!bubble) return;
      bubble.classList.toggle('hidden', count === 0);
      var num  = bubble.querySelector('[aria-hidden="true"]');
      var vis  = bubble.querySelector('.visually-hidden');
      if (num) num.textContent = count;
      if (vis) vis.textContent = count + ' ' + (count === 1 ? itemSingular : itemPlural);
    });
  }

  /* ─────────────────────────────────────────────────────────
     syncTotals — subtotal + footer visibility
  ───────────────────────────────────────────────────────── */
  function syncTotals(totalCents, count) {
    var fmt = money(totalCents);
    if (subtotalEl) subtotalEl.textContent = fmt;
    if (btnTotalEl) btnTotalEl.textContent = fmt;
    if (footerEl)   footerEl.classList.toggle('is-hidden', count === 0);
  }

  /* ─────────────────────────────────────────────────────────
     syncShippingBar
     Bug 1 fix — the bar element may not exist in the DOM at all when the
     cart was empty at page load (Liquid renders it conditionally).
     Solution: if the bar is needed, build and insert it; if not, remove it.
     We never cache the bar's DOM refs — always look them up live.
  ───────────────────────────────────────────────────────── */
  function syncShippingBar(totalCents, itemCount) {
    /* Bar disabled in Theme Editor — remove element if present, then exit */
    if (!showBar) {
      var existingOff = document.getElementById(ID.shippingBar);
      if (existingOff) existingOff.parentNode.removeChild(existingOff);
      return;
    }

    /* Empty cart — remove bar entirely so {{amount}} is never visible */
    if (itemCount === 0) {
      var existingEmpty = document.getElementById(ID.shippingBar);
      if (existingEmpty) existingEmpty.parentNode.removeChild(existingEmpty);
      return;
    }

    /* ── Cart has items ─────────────────────────────────────── */

    /* Ensure the bar element exists in the DOM.
       If it was omitted server-side (empty cart at page load) we inject it
       as the first child of #lm-cart-body, before #lm-cart-items. */
    var barEl = document.getElementById(ID.shippingBar);
    if (!barEl) {
      barEl = buildBarElement();
      /* Insert before items list inside the scrollable body */
      if (bodyEl && itemsEl && itemsEl.parentNode === bodyEl) {
        bodyEl.insertBefore(barEl, itemsEl);
      } else if (bodyEl) {
        bodyEl.insertBefore(barEl, bodyEl.firstChild);
      } else {
        /* Last resort: insert before the items container wherever it is */
        if (itemsEl && itemsEl.parentNode) {
          itemsEl.parentNode.insertBefore(barEl, itemsEl);
        }
      }
    }

    /* Always make sure it's visible */
    barEl.style.display = '';

    /* Exit early if no threshold is configured */
    if (!thresholdCents) return;

    /* Calculate progress */
    var pct       = Math.min(100, Math.round((totalCents / thresholdCents) * 100));
    var remaining = Math.max(0, thresholdCents - totalCents);
    var unlocked  = totalCents >= thresholdCents;

    /* Re-query children live — they may have just been created above */
    var fillEl = document.getElementById(ID.progressFill);
    var txtEl  = document.getElementById(ID.shippingText);

    if (fillEl) {
      fillEl.style.width = pct + '%';
      fillEl.setAttribute('aria-valuenow', pct);
      fillEl.classList.toggle('is-complete', unlocked);
    }

    if (txtEl) {
      /* {{amount}} is always replaced with a real money string here */
      txtEl.textContent = unlocked
        ? unlockedMsg
        : progressMsg.replace('{{amount}}', money(remaining));
    }
  }

  /* Builds the shipping bar DOM node from scratch */
  function buildBarElement() {
    var bar = document.createElement('div');
    bar.id        = ID.shippingBar;
    bar.className = 'lm-cart-drawer__shipping-bar';
    bar.innerHTML = (
      '<p class="lm-cart-drawer__shipping-text" id="' + ID.shippingText + '"></p>' +
      '<div class="lm-cart-drawer__progress-track">' +
        '<div' +
          ' class="lm-cart-drawer__progress-fill"' +
          ' id="' + ID.progressFill + '"' +
          ' role="progressbar"' +
          ' style="width:0%"' +
          ' aria-valuenow="0"' +
          ' aria-valuemin="0"' +
          ' aria-valuemax="100"' +
        '></div>' +
      '</div>'
    );
    return bar;
  }

  /* ─────────────────────────────────────────────────────────
     syncItems — replaces items list HTML
  ───────────────────────────────────────────────────────── */
  function syncItems(cart) {
    if (!itemsEl) return;
    itemsEl.innerHTML = cart.item_count === 0
      ? buildEmpty()
      : cart.items.map(buildItem).join('');
  }

  /* ─────────────────────────────────────────────────────────
     HTML builders
  ───────────────────────────────────────────────────────── */
  function buildItem(item) {
    var imgSrc = item.featured_image && item.featured_image.url
      ? resizeImg(item.featured_image.url, 240) : null;

    var imgHTML = imgSrc
      ? '<img class="lm-cart-drawer__item-img" src="' + imgSrc + '" alt="' + esc(item.product_title) + '" loading="lazy">'
      : '<div class="lm-cart-drawer__item-img lm-cart-drawer__item-img--placeholder">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" width="36" height="36" aria-hidden="true">' +
            '<rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 15l5-5 4 4 3-3 6 6"/>' +
          '</svg></div>';

    var variantHTML = (item.variant_title && item.variant_title !== 'Default Title')
      ? '<p class="lm-cart-drawer__item-variant">' + esc(item.variant_title) + '</p>' : '';

    var spHTML = item.selling_plan_allocation
      ? '<p class="lm-cart-drawer__item-variant">' + esc(item.selling_plan_allocation.selling_plan.name) + '</p>' : '';

    var compareHTML = item.original_line_price !== item.final_line_price
      ? '<s class="lm-cart-drawer__item-price lm-cart-drawer__item-price--compare">' + money(item.original_line_price) + '</s>' : '';

    var priceClass = 'lm-cart-drawer__item-price' +
      (item.original_line_price !== item.final_line_price ? ' lm-cart-drawer__item-price--sale' : '');

    var k = esc(item.key);

    return (
      '<div class="lm-cart-drawer__item" data-key="' + k + '">' +
        '<a href="' + esc(item.url) + '" class="lm-cart-drawer__item-img-link" tabindex="-1" aria-hidden="true">' + imgHTML + '</a>' +
        '<div class="lm-cart-drawer__item-details">' +
          '<div class="lm-cart-drawer__item-top">' +
            '<div class="lm-cart-drawer__item-meta">' +
              '<a href="' + esc(item.url) + '" class="lm-cart-drawer__item-title">' + esc(item.product_title) + '</a>' +
              variantHTML + spHTML +
            '</div>' +
            '<div class="lm-cart-drawer__item-price-wrap">' +
              compareHTML +
              '<span class="' + priceClass + '">' + money(item.final_line_price) + '</span>' +
            '</div>' +
          '</div>' +
          '<div class="lm-cart-drawer__item-bottom">' +
            '<div class="lm-cart-drawer__qty">' +
              '<button class="lm-cart-drawer__qty-btn" data-action="decrease" data-key="' + k + '" aria-label="Decrease quantity" type="button">' +
                '<svg width="10" height="2" viewBox="0 0 10 2" fill="none" aria-hidden="true"><path d="M1 1H9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>' +
              '</button>' +
              '<span class="lm-cart-drawer__qty-num" id="qty-' + k + '">' + item.quantity + '</span>' +
              '<button class="lm-cart-drawer__qty-btn" data-action="increase" data-key="' + k + '" aria-label="Increase quantity" type="button">' +
                '<svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true"><path d="M5 1V9M1 5H9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>' +
              '</button>' +
            '</div>' +
            '<button class="lm-cart-drawer__remove" data-key="' + k + '" aria-label="Remove ' + esc(item.product_title) + '" type="button">' + esc(removeLabel) + '</button>' +
          '</div>' +
        '</div>' +
      '</div>'
    );
  }

  function buildEmpty() {
    return (
      '<div class="lm-cart-drawer__empty">' +
        '<div class="lm-cart-drawer__empty-icon" aria-hidden="true">' +
          '<svg width="52" height="52" viewBox="0 0 48 48" fill="none">' +
            '<path d="M6 6H10.5L14.1 28.5H36L39 14H12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
            '<circle cx="18" cy="36" r="2" stroke="currentColor" stroke-width="1.5"/>' +
            '<circle cx="32" cy="36" r="2" stroke="currentColor" stroke-width="1.5"/>' +
          '</svg>' +
        '</div>' +
        '<p class="lm-cart-drawer__empty-title">' + esc(emptyTitle) + '</p>' +
        '<p class="lm-cart-drawer__empty-subtitle">' + esc(emptySubtitle) + '</p>' +
        '<a href="' + esc(emptyBtnUrl) + '" class="lm-cart-drawer__empty-btn">' + esc(emptyBtnText) + '</a>' +
      '</div>'
    );
  }

  /* ─────────────────────────────────────────────────────────
     Utilities
  ───────────────────────────────────────────────────────── */
  function setLoading(el, on) {
    if (el) el.classList.toggle('is-loading', on);
  }

  function money(cents) {
    if (typeof cents !== 'number') cents = parseInt(cents, 10) || 0;
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, window.Shopify.money_format || '${{amount}}');
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function resizeImg(url, size) {
    return url.replace(/(\.(jpg|jpeg|gif|png|webp|svg))(\?.*)?$/i, '_' + size + 'x$1$3');
  }

  function esc(val) {
    return String(val == null ? '' : val)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /* ─────────────────────────────────────────────────────────
     Boot
  ───────────────────────────────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Public API */
  window.LMCartDrawer = {
    open:    openDrawer,
    close:   closeDrawer,
    refresh: refreshDrawer,
    addById: addToCartById,
  };

})();
